/**
 * Layouts contributed by the Naumene firm app.
 *
 * Two kinds, and the distinction is the important part:
 *
 *   staff routes      the dashboard shell, which carries the AuthGuard.
 *                     EVERY staff page must be listed below — a page left
 *                     out still renders, in the marketing layout, without a
 *                     sidebar and without the guard. It looks like a styling
 *                     bug and is actually an unauthenticated page.
 *   /f/:slug          a PROSPECTIVE CLIENT filling in a form. No guard, no
 *   /s/:token         navigation, no sign-in prompt. Ordered ahead of
 *                     everything so nothing else can claim them.
 *
 * Getting the second one wrong is not a cosmetic bug: routing /f through a
 * guarded shell would redirect every client to a login page they can never
 * pass, and the firm would see silence rather than an error.
 *
 * @llm-rule AVOID: Letting /f match a guarded or navigation-bearing layout
 */
import { DashboardLayoutRoute } from './DashboardLayoutRoute';
import { PublicFormLayout } from './PublicFormLayout';
import { SigningLayout } from './SigningLayout';
import { FocusLayoutRoute } from './FocusLayoutRoute';
import type { LayoutEntry } from './layouts';

export const entries: LayoutEntry[] = [
  { match: (p) => p === '/f' || p.startsWith('/f/'), Layout: PublicFormLayout, order: 100 },
  // /s/:token — a client signing. Same reasoning as /f: no guard, no nav.
  { match: (p) => p === '/s' || p.startsWith('/s/'), Layout: PublicFormLayout, order: 101 },
  /* /book/:slug — a client picking a consultation time. Public by design, so
   * it belongs here for the bare layout and explicitly NOT in the guarded
   * prefix list below. */
  { match: (p) => p === '/book' || p.startsWith('/book/'), Layout: PublicFormLayout, order: 102 },
  /* /d/:token — a client sending in the documents their firm asked for. Same
   * shell as the form and the signing page for the same reason: no account,
   * no navigation, nothing to click but the task. */
  { match: (p) => p === '/d' || p.startsWith('/d/'), Layout: PublicFormLayout, order: 103 },
  /* /a/:token — a client's own appointment: see it, move it, cancel it. */
  { match: (p) => p === '/a' || p.startsWith('/a/'), Layout: PublicFormLayout, order: 104 },
  /* /sign/:token — a signer on a signing packet. Public like /s, but the
   * document takes the whole screen, so it has its own bare shell. */
  { match: (p) => p === '/sign' || p.startsWith('/sign/'), Layout: SigningLayout, order: 105 },
  /* /envelope/:id — the packet editor. STAFF, so guarded; full-screen, so
   * not the sidebar shell. FocusLayoutRoute carries the same guards. */
  { match: (p) => p === '/envelope' || p.startsWith('/envelope/'), Layout: FocusLayoutRoute, order: 106 },

  /* `/submission/:id` is a STAFF page — it shows one prospect's answers — so
   * it must be here. Left out, it would still render, in the marketing
   * layout, with no sidebar and NO AUTHGUARD: a stranger with the link would
   * read somebody's intake form. See the note at the top of this file. */
  ...['/form', '/pipeline', '/matter', '/stage', '/overview', '/follow-up', '/scoring', '/submission', '/template', '/appointment', '/calendar', '/email-template', '/notification', '/integration'].map((prefix, i) => ({
    match: (p: string) => p === prefix || p.startsWith(`${prefix}/`),
    Layout: DashboardLayoutRoute,
    order: 210 + i,
  })),
];
